import React from "react";
import { Link } from "react-router-dom";
import { Target, Eye, HeartHandshake, Sparkles, Shield, BookOpen } from "lucide-react";
import SectionHeader from "@/components/site/SectionHeader";

const VALUES = [
  { icon: HeartHandshake, title: "Manaakitanga", text: "We honour each rangatahi with care, respect, and dignity." },
  { icon: Sparkles, title: "Whanaungatanga", text: "We build trust through real, lasting relationships." },
  { icon: Target, title: "Evidence-informed", text: "Our work is grounded in current research and Hauora best practice." },
  { icon: Eye, title: "Equity", text: "We work to close gaps in access for those most often left behind." },
];

const FOCUS = [
  { icon: Shield, title: "Consent education", text: "Building everyday language and confidence around consent, boundaries and respect." },
  { icon: BookOpen, title: "Sexual health literacy", text: "Plain, accurate information about bodies, relationships, and choices." },
  { icon: Sparkles, title: "STI awareness", text: "Reducing stigma and improving understanding of prevention, testing and care." },
  { icon: Eye, title: "Equitable access", text: "Reaching rangatahi who are often missed by mainstream services." },
];

export default function About() {
  return (
    <>
      <section className="relative">
        <div className="container-x pt-20 pb-16 sm:pt-28">
          <div className="max-w-4xl">
            <span className="label-eyebrow">— Our story</span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-7xl font-heading font-semibold tracking-tighter leading-[1.02] text-balance">
              Sexual health &amp; wellbeing education for rangatahi — and the people who support them.
            </h1>
            <p className="mt-8 text-lg text-ink/65 max-w-2xl leading-relaxed">
              Aoturoa Academy Charitable Trust (CC58012) is an Auckland-based charitable trust founded by Shailesh Bagwe.
              We work to improve sexual health knowledge, confidence and wellbeing across Aotearoa — both through direct
              education for rangatahi, and through professional workshops for the educators, organisations and services
              who support them.
            </p>
          </div>
        </div>
      </section>

      <section className="container-x pb-20">
        <div className="rounded-3xl overflow-hidden aspect-[16/8]">
          <img src="https://images.pexels.com/photos/8457972/pexels-photo-8457972.jpeg" alt="Youth and community in Aotearoa" className="w-full h-full object-cover" />
        </div>
      </section>

      <section className="section-y">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <span className="label-eyebrow">— Mission</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-heading font-semibold leading-tight">
              Building knowledge, confidence and wellbeing — across every part of the network around rangatahi.
            </h2>
          </div>
          <div className="lg:col-span-7 text-ink/75 leading-relaxed space-y-5 text-lg">
            <p>
              Our mission is to strengthen sexual health and wellbeing outcomes for young people in Aotearoa by providing
              evidence-informed education that works on two levels: directly with rangatahi, and with the educators,
              professionals and organisations who walk alongside them.
            </p>
            <p>
              We align our work with the New Zealand Health &amp; Hauora curriculum and the wellbeing priorities set by
              Te Whatu Ora. Aoturoa Academy provides education, workshops and professional development —
              <span className="text-ink"> we are not a clinical healthcare provider</span>. Where clinical support is
              needed, we help connect rangatahi and whānau to appropriate services.
            </p>
            <div className="pt-6 border-t border-stone">
              <span className="label-eyebrow">— Vision</span>
              <p className="mt-4 text-ink text-xl font-heading">
                A future where every rangatahi in Aotearoa has the knowledge, language, and support to thrive — and
                every professional supporting them has the confidence to help.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-sand">
        <div className="container-x py-20 lg:py-28">
          <SectionHeader eyebrow="Why we exist" title="Why this initiative was created." />
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8 text-ink/75 leading-relaxed">
            <p>
              Young people in Aotearoa are growing up in a world that talks loudly about sex and relationships — but
              rarely with honesty, inclusivity, or cultural understanding. Existing sexual health education can feel
              clinical, fragmented, or out of touch with the diverse lives of rangatahi today.
            </p>
            <p>
              This initiative was created to help fill that gap: a community-grounded, youth-focused, culturally responsive
              approach that treats sexual health as part of overall wellbeing — and treats every young person with the
              respect they deserve.
            </p>
          </div>
        </div>
      </section>

      <section className="section-y">
        <div className="container-x">
          <SectionHeader eyebrow="Our focus" title="What we are developing." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FOCUS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="card-surface">
                <Icon className="w-6 h-6 text-crimson mb-5" />
                <h3 className="font-heading font-semibold text-lg">{title}</h3>
                <p className="mt-2 text-ink/65 text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-bone">
        <div className="container-x section-y">
          <SectionHeader eyebrow="Our values" title="What we stand for." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map(({ icon: Icon, title, text }) => (
              <div key={title} className="bg-white border border-stone rounded-2xl p-7">
                <Icon className="w-6 h-6 text-crimson mb-5" />
                <h3 className="font-heading font-semibold text-lg">{title}</h3>
                <p className="mt-2 text-ink/65 text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink text-bone">
        <div className="container-x py-20 lg:py-24 text-center">
          <span className="label-eyebrow">— Looking ahead</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold tracking-tight text-balance max-w-3xl mx-auto">
            Auckland today. Aotearoa tomorrow.
          </h2>
          <p className="mt-5 text-bone/70 max-w-xl mx-auto">
            We are an Auckland-based initiative with future national aspirations — growing carefully, in step with community.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link to="/partners" data-testid="about-partner-btn" className="btn-crimson">Community Collaboration</Link>
            <Link to="/contact" data-testid="about-connect-btn" className="btn-ghost-light">Connect With Us</Link>
          </div>
        </div>
      </section>
    </>
  );
}
